import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Confetti from 'react-confetti';
import { registerProvider } from '@/services/providerService';
import { useTranslation } from 'react-i18next';

export default function RegisterProviderSuccess() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState(t('services.registerProviderSuccess.processingPayment'));

  useEffect(() => {
    const processRegistration = async () => {
      const success = searchParams.get('success');
      const token = searchParams.get('token');
      const subscriptionId = searchParams.get('subscription_id');

      console.log('Processing registration callback:', { success, token, subscriptionId });

      if (success === 'false') {
        setStatus('error');
        setMessage(t('services.registerProviderSuccess.paymentCancelled'));
        toast.error(t('services.registerProviderSuccess.paymentCancelledToast'));
        
        // Clean up uploaded files
        const fileUrls = sessionStorage.getItem('pendingProviderFileUrls');
        if (fileUrls) {
          sessionStorage.removeItem('pendingProviderFileUrls');
        }
        
        setTimeout(() => navigate('/register-provider'), 3000);
        return;
      }

      if (!token || !subscriptionId) {
        setStatus('error');
        setMessage(t('services.registerProviderSuccess.invalidPayment'));
        toast.error(t('services.registerProviderSuccess.invalidPaymentToast'));
        return;
      }

      try {
        // Recuperar datos del formulario y URLs de archivos desde sessionStorage
        const pendingData = sessionStorage.getItem('pendingProviderRegistration');
        const pendingFileUrls = sessionStorage.getItem('pendingProviderFileUrls');

        if (!pendingData) {
          throw new Error(t('services.registerProviderSuccess.registrationDataNotFound'));
        }

        if (!pendingFileUrls) {
          setStatus('error');
          setMessage(t('services.registerProviderSuccess.dataNotFound'));
          toast.error(t('services.registerProviderSuccess.dataNotFound'));
          setTimeout(() => navigate('/register-provider'), 3000);
          return;
        }

        const { formData, coordinates, services, languages, schedule } = JSON.parse(pendingData);
        const { photoUrls, credentialUrl, idFrontUrl, idBackUrl } = JSON.parse(pendingFileUrls);

        console.log('Files already uploaded, registering provider with edge function...');
        // Registrar proveedor con las URLs de los archivos ya subidos
        await registerProvider({
          ...formData,
          latitude: coordinates.lat,
          longitude: coordinates.lon,
          photos: photoUrls,
          services,
          languages,
          schedule,
          credential_document_url: credentialUrl,
          paypal_subscription_id: subscriptionId
        });

        console.log('Provider registered, updating profile...');
        // Actualizar provider_registration_completed
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ provider_registration_completed: true })
          .eq('id', user!.id);

        if (profileError) {
          console.error('Error updating profile:', profileError);
        }

        // Limpiar sessionStorage
        sessionStorage.removeItem('pendingProviderRegistration');
        sessionStorage.removeItem('pendingProviderFileUrls');

        console.log('Registration completed successfully!');
        setStatus('success');
        setMessage(t('services.registerProviderSuccess.registrationSuccess'));
        toast.success(t('services.registerProviderSuccess.welcomeMessage'));

        setTimeout(() => navigate('/provider-dashboard'), 3000);

      } catch (error: any) {
        console.error('Error processing registration:', error);
        setStatus('error');
        setMessage(error.message || t('services.registerProviderSuccess.registrationFailed'));
        toast.error(t('services.registerProviderSuccess.registrationError'));
      }
    };

    if (user) {
      processRegistration();
    }
  }, [searchParams, navigate, user]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      {status === 'success' && <Confetti recycle={false} numberOfPieces={500} />}
      
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">{t('services.registerProviderSuccess.processing')}</h2>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-green-700 mb-2">{t('services.registerProviderSuccess.success')}</h2>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-700 mb-2">{t('services.registerProviderSuccess.error')}</h2>
            </>
          )}
          
          <p className="text-muted-foreground">{message}</p>
        </CardContent>
      </Card>
    </div>
  );
}
